import { css } from 'astroturf';
import React from 'react';

const LayoutGrid = ({ className, strokeWidth = 2 }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
  </svg>
);

const Columns3 = ({ className, strokeWidth = 2 }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <line x1="9" y1="3" x2="9" y2="21" />
    <line x1="15" y1="3" x2="15" y2="21" />
  </svg>
);

const Anchor = ({ className, strokeWidth = 2 }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="5" r="3" />
    <line x1="12" y1="22" x2="12" y2="8" />
    <path d="M5 12H2a10 10 0 0 0 20 0h-3" />
  </svg>
);

const MousePointerClick = ({ className, strokeWidth = 2 }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 4.1 12 6" />
    <path d="m5.1 8-2.9-.8" />
    <path d="m6 12-1.9 2" />
    <path d="M7.2 2.2 8 5.1" />
    <path d="M9.037 9.69a.498.498 0 0 1 .653-.653l11 4.5a.5.5 0 0 1-.074.949l-4.349 1.041a1 1 0 0 0-.74.739l-1.04 4.35a.5.5 0 0 1-.95.074z" />
  </svg>
);

const icons = {
  flex: Columns3,
  grid: LayoutGrid,
  anchor: Anchor,
  invoker: MousePointerClick,
};

const styles = css`
  .cardRoot {
    display: flex;
    flex-direction: column;
    overflow: hidden;

    .cardHeader {
      padding-left: 1rem;
      padding-right: 1rem;
      padding-top: 0.5rem;
      padding-bottom: 0.5rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      padding-bottom: 0.125rem;
    }

    .titleGroup {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .titleIcon {
      width: 1rem;
      height: 1rem;
      color: var(--sheet-emerald-300);
    }

    .titleText {
      font-size: 1rem;
      line-height: 1.5rem;
      font-weight: 600;
      color: #ffffff;
      font-family: var(--font-inter);
    }

    img.logo {
      height: 1.5rem;
      width: auto;
    }

    .cardBody {
      flex: 1 1 0%;
      display: flex;
      flex-direction: column;
      padding: 0.75rem;
      padding-top: 0.25rem;
      padding-bottom: 0;
      border-radius: 0.75rem;
      margin-left: 0.375rem;
      margin-right: 0.375rem;
      margin-bottom: 0.375rem;
    }

    .content {
      flex: 1 1 0%;
      font-size: 11px;
      line-height: 1.35;
      line-height: 1.625;
      color: var(--sheet-slate-200);
    }

    .footer {
      padding-bottom: 0.5rem;
    }

    .footerText {
      font-size: 11px;
      line-height: 1.25;
      color: var(--sheet-slate-400);
      text-align: center;
      font-family: var(--font-inter);
    }

    .brand {
      color: var(--sheet-emerald-400);
      font-weight: 600;
    }

    @media print {
      .cardRoot {
        break-inside: avoid;
      }
    }
  }
`;

export function CheatsheetCard({ title, icon, children, withBleed = false }) {
  const Icon = icon ? icons[icon] : null;

  return (
    <article
      className={styles.cardRoot}
      style={{
        zoom: 1,
        width: withBleed ? '154mm' : '148mm',
        height: withBleed ? '216mm' : '210mm',
        padding: withBleed ? '3mm' : 0,
        borderRadius: withBleed ? 0 : '3mm',
      }}
    >
      <header className={styles.cardHeader}>
        <div className={styles.titleGroup}>
          {Icon && <Icon className={styles.titleIcon} strokeWidth={2} />}
          <h1 className={styles.titleText}>{title}</h1>
        </div>
        <img src="/img/brand/polypane-logo-dark.svg" alt="Polypane logo" className={styles.logo} />
      </header>

      <div
        className={styles.cardBody}
        style={{
          background: 'linear-gradient(180deg, #2a3441 0%, #1e2832 50%, #1a242d 100%)',
          borderRadius: 'calc(3.5mm - 5px)',
        }}
      >
        <div className={styles.content}>{children}</div>

        <footer className={styles.footer}>
          <p className={styles.footerText}>
            <span className={styles.brand}>Polypane.app</span> for professional web developers who care about their
            craft.
          </p>
        </footer>
      </div>
    </article>
  );
}
